import React, { lazy } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Switch, useHistory, useLocation } from 'react-router-dom';

import Dialog from '@liquidweb-dev/undercurrent/Dialog';
import DialogTitle from '@liquidweb-dev/undercurrent/DialogTitle';
import DialogContent from '@liquidweb-dev/undercurrent/DialogContent';
import DialogActions from '@liquidweb-dev/undercurrent/DialogActions';
import Button from '@liquidweb-dev/undercurrent/Button';
import toast from '@liquidweb-dev/undercurrent/toast';
import SupportLayout from 'components/standard/SupportLayout';
import { username as usernameSelector } from 'modules/auth/authSelectors';
import useSupportRequestAuthenticate from 'modules/queries/support/request/useAuthenticate';
import { roles } from 'utility/constants/roles';
import RoleRestrictedRoute from 'utility/routing/RoleRestrictedRoute';

const MyCases = lazy(() => import('containers/pages/support/MyCases'));
const FeaturedHelpDocs = lazy(() => import('containers/pages/support'));
const LiquidWebStatusReports = lazy(
	() => import('containers/pages/support/LiquidWebStatusReports'),
);

const AuthenticateSupportRequest = () => {
	const location = useLocation();
	const history = useHistory();
	const loggedInUser = useSelector(usernameSelector);

	const searchParams = new URLSearchParams(location.search);
	const caseId = searchParams.get('case_id') || searchParams.get('id');

	const { mutate: authenticate, isLoading } = useSupportRequestAuthenticate();

	const handleClose = () => history.push('/');

	const handleConfirm = () => {
		if (!caseId) {
			toast.danger('It looks like your link is missing a Case ID');
			return;
		}

		authenticate(
			{ id: caseId, username: loggedInUser },
			{
				onSuccess: () => {
					handleClose();
				},
				onError: () => {
					toast.danger('There was an issue with your link');
				},
			},
		);
	};

	return (
		<Dialog open onClose={handleClose}>
			<DialogTitle onClose={handleClose}>Confirmation Needed</DialogTitle>
			<DialogContent>
				You are signed in as {loggedInUser}. Please confirm you would like to
				authenticate this support request with this account.
			</DialogContent>
			<DialogActions>
				<Button variant="secondary" onClick={handleClose}>
					Cancel
				</Button>
				<Button onClick={handleConfirm} loading={isLoading}>
					Confirm
				</Button>
			</DialogActions>
		</Dialog>
	);
};

const SupportRouter = () => {
	const { pathname } = useLocation();
	return (
		<SupportLayout tabsValue={pathname}>
			<Switch>
				<RoleRestrictedRoute
					// legacy route
					exact
					whitelistedRoles={[
						roles.PURCHASER,
						roles.TECHNICIAN,
						roles.SUSPENDED_OWNER,
					]}
					path="/support/mytickets"
					component={() => <Redirect to="/support/mycases" />}
				/>
				<RoleRestrictedRoute
					exact
					whitelistedRoles={[
						roles.PURCHASER,
						roles.TECHNICIAN,
						roles.SUSPENDED_OWNER,
					]}
					path="/support/mycases"
					component={MyCases}
				/>
				<RoleRestrictedRoute
					exact
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
					path="/support/request/authenticate"
					component={AuthenticateSupportRequest}
				/>
				<RoleRestrictedRoute
					exact
					whitelistedRoles={[
						roles.PURCHASER,
						roles.TECHNICIAN,
						roles.SUSPENDED_OWNER,
					]}
					path="/support/status"
					component={LiquidWebStatusReports}
				/>
				<RoleRestrictedRoute
					exact
					whitelistedRoles={[
						roles.PURCHASER,
						roles.TECHNICIAN,
						roles.SUSPENDED_OWNER,
					]}
					path="/support"
					component={FeaturedHelpDocs}
				/>
			</Switch>
		</SupportLayout>
	);
};

export default SupportRouter;
